import { isEqual, map, mapValues, noop, values } from 'lodash';
import { useLayoutEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FilterBar } from 'common/components/FilterBar';
import { type FilterParameterConfiguration } from 'common/types/reportFilters';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import {
  updateFilterParameterConfiguration,
  updateAllFilterParameterConfigurations,
  updateDraftFilterParameterConfiguration,
  updateAllDraftFilterParameterConfigurations,
} from 'store/TopLevelActions';
import {
  getAllDataSourceCCVs,
  getDataSourceMetadata,
  getDraftFilterParameterConfigurations,
  getFilterParameterConfigurations,
  getHasReportBeenRun,
  getHasUserMadeFilterChanges,
} from 'store/selectors/DataSourceSelectors';
import { isFlexibleStory } from 'lib/FlexibleLayoutUtils';
import { inPrintMode } from 'util/in-print-mode';
import { EnvironmentFactory } from 'StorytellerEnvironment';
import { selectStoryContainsAMeasure } from 'store/selectors/StorySelectors/Stories';

export const FilterBarContainer = ({ editMode = false }: { editMode: boolean }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isEmailMode = EnvironmentFactory().LOADED_AS_EMAIL;
  // this is used for Fargo initially
  const canBatchRun = FeatureFlags.valueOrDefault('enable_batch_running_filters');
  // The expandable global filter bar requires both enable_batch_running_filters and enable_updated_gfb.
  const useExpandableGfbLayout = canBatchRun && FeatureFlags.valueOrDefault('enable_updated_gfb');
  // we only want to use applyAllMode when both the batch running ff is true and we are not in edit mode
  const useApplyAllFilters = canBatchRun && !editMode && !inPrintMode() && !isEmailMode;
  const useApplyAllLayout = canBatchRun && !editMode;

  const dispatch = useDispatch();
  const metadata = useSelector(getDataSourceMetadata, isEqual);
  const appliedFilterParameterConfigurations = useSelector(getFilterParameterConfigurations, isEqual);
  const draftFilterParameterConfigurations = useSelector(getDraftFilterParameterConfigurations, isEqual);
  const allDataSourceCCVs = useSelector(getAllDataSourceCCVs);
  const hasMadeFilterChanges = useSelector(getHasUserMadeFilterChanges);
  const hasReportBeenRun = useSelector(getHasReportBeenRun);

  const onUpdateAllFilterParameters = (updatedConfigs: FilterParameterConfiguration[]): void => {
    dispatch(updateAllFilterParameterConfigurations({ updatedConfigs }));
  };

  const onUpdateAllFilterParametersApplyAllVersion = (
    updatedConfigs: FilterParameterConfiguration[],
    clearAll?: boolean,
  ): void => {
    if (clearAll) {
      dispatch(updateAllDraftFilterParameterConfigurations({ updatedConfigs }));
    } else {
      dispatch(updateAllFilterParameterConfigurations({ updatedConfigs }));
    }
  };

  const onUpdateSingleFilterParameterDraftState = (
    updatedConfig: FilterParameterConfiguration,
    index: number,
  ) => {
    dispatch(updateDraftFilterParameterConfiguration({ index, updatedConfig }));
  };

  const onUpdateSingleFilterParameter = (updatedConfig: FilterParameterConfiguration, index: number) => {
    dispatch(updateFilterParameterConfiguration({ index, updatedConfig }));
  };

  // In classic edit mode, story needs to be prompted to re-render
  useLayoutEffect(() => {
    if (!editMode || isFlexibleStory() || !containerRef.current) {
      return;
    }
    const expandControl = containerRef.current.querySelector('.btn-expand-control');
    const rerenderStory = (): void => {
      // The setTimeout is required to ensure that the height change event
      // happens _after_ the filter bar is actually expanded
      setTimeout(
        () =>
          containerRef.current?.dispatchEvent(new CustomEvent('component::height-change', { bubbles: true })),
        0,
      );
    };

    expandControl?.addEventListener('click', rerenderStory);

    return (): void => {
      expandControl?.removeEventListener('click', rerenderStory);
    };
  }, [containerRef, editMode]);

  const isMeasureInStory = useSelector(selectStoryContainsAMeasure);

  return (
    <div ref={containerRef}>
      <FilterBar
        clientContextVariables={allDataSourceCCVs}
        onUpdate={noop}
        onUpdateAllFilterParameters={
          useApplyAllFilters ? onUpdateAllFilterParametersApplyAllVersion : onUpdateAllFilterParameters
        }
        onUpdateSingleFilterParameter={
          useApplyAllFilters ? onUpdateSingleFilterParameterDraftState : onUpdateSingleFilterParameter
        }
        columns={mapValues(metadata, ({ columns }) => values(columns))}
        computedColumns={[]}
        onInEditFilterChange={noop}
        isReadOnly={!editMode}
        // If metadata has not loaded for all datasources yet, then the FilterBar will not render anything.
        dataSource={map(metadata, ({ domainCName, id }) => ({ datasetUid: id, domain: domainCName! }))}
        filterParameterConfigurations={
          useApplyAllFilters ? draftFilterParameterConfigurations : appliedFilterParameterConfigurations
        }
        editMode={editMode}
        pendoIds={{
          filterParameterControlContainer: 'global-filter-container-dropdown',
          expandButton: 'global-filter-expand-control',
          resetAllFilters: 'reset-filters-gfb',
        }}
        showApplyAll={useApplyAllFilters}
        globalFilterBarLayout={useApplyAllLayout || useExpandableGfbLayout}
        expandableGfbLayout={useExpandableGfbLayout}
        showFiltersExpanded={useApplyAllLayout}
        independentTextFilters={useApplyAllFilters}
        isPrintMode={inPrintMode() && canBatchRun}
        isEmailMode={isEmailMode}
        isMeasureInStory={isMeasureInStory}
        hasMadeFilterChanges={hasMadeFilterChanges}
        hasReportBeenRun={hasReportBeenRun}
      />
    </div>
  );
};
